import { SortOrder } from "@/enums/SortOrder";
import type { Row } from "@rocicorp/zero";

/**
 * Filter a list of dossiers based on their name and a search query
 *
 * @param searchString - The string to search for
 * @param dossiers - The array to search in
 * @returns Filtered list of dossiers
 */
export function searchAllDossiers(
	searchString: string,
	dossiers?: Row["dossier"][],
): Row["dossier"][] {
	const matches: Row["dossier"][] = [];
	const normalizedSearch = searchString.toLowerCase().trim();

	dossiers?.forEach((d) => {
		const dossierName = d.name.toLowerCase().trim();
		if (dossierName.includes(normalizedSearch)) {
			matches.push(d);
		}
	});

	return matches;
}

/**
 * Sorts an array based on the column and the order.
 *
 * @param toSort - The array to sort
 * @param column - The column to sort the array on
 * @param order - The order to sort the array
 * @returns The sorted array
 */
export function sortMatches<T>(
	toSort: T[],
	column: keyof T,
	order: SortOrder,
): T[] {
	if (order !== SortOrder.Ascending && order !== SortOrder.Descending) {
		return toSort;
	}

	return toSort?.toSorted((dossierA, dossierB) => {
		let valueA = dossierA[column];
		let valueB = dossierB[column];

		if (order === SortOrder.Descending) {
			[valueA, valueB] = [valueB, valueA];
		}

		if (typeof valueA === "string" && typeof valueB === "string") {
			return valueA.localeCompare(valueB);
		}
		if (valueA instanceof Date && valueB instanceof Date) {
			return Number(valueA > valueB);
		}

		if (typeof valueA === "number" && typeof valueB === "number") {
			return valueA - valueB;
		}

		return -1;
	});
}
